// iter159 — Admin panel: per-host "Toolkit auto-login" switches + last 50 audit rows.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { SsoLinkedAccounts } from "@/components/SsoLinkedAccounts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HostSwitch = ({ host, label, checked, current, busy, onChange, testPrefix = "sso" }) => (
  <div className="flex items-center justify-between rounded-xl border border-border bg-sand-50 px-3 py-2" data-testid={`${testPrefix}-switch-row-${host}`}>
    <div>
      <p className="text-sm font-semibold text-ink-900">{label}{current && <span className="ml-2 rounded-full bg-ink-900 px-2 py-0.5 text-[0.6rem] uppercase text-white">this host</span>}</p>
      <p className="text-xs text-ink-700">{checked ? "ON" : "OFF"}</p>
    </div>
    <Switch checked={checked} disabled={busy} onCheckedChange={(v) => onChange(host, v)} data-testid={`${testPrefix}-switch-${host}`} />
  </div>
);

export const SsoToolkitPanel = () => {
  const [data, setData] = useState({ switches: { preview: false, live: false }, currentHost: "", audit: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/sso/admin/settings`); setData(r.data); }
    catch { toast.error("Could not load auto-login settings"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (host, enabled) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/sso/admin/switch`, { host, enabled });
      setData((d) => ({ ...d, switches: r.data.switches }));
      toast.success(`Toolkit auto-login (${host}) ${enabled ? "ON" : "OFF"}`);
    } catch { toast.error("Could not change switch"); }
    finally { setBusy(false); }
  };

  return (
    <section className="mb-5 rounded-2xl border border-border bg-white p-4 shadow-sm" data-testid="sso-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-ink-700" />
          <h2 className="font-heading text-sm font-bold text-ink-900">Toolkit auto-login</h2>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="sso-reload-btn"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <HostSwitch host="preview" label="Preview host" checked={data.switches.preview} current={data.currentHost === "preview"} busy={busy} onChange={toggle} />
        <HostSwitch host="live" label="LIVE host" checked={data.switches.live} current={data.currentHost === "live"} busy={busy} onChange={toggle} />
      </div>
      <SsoLinkedAccounts />
      <AuditTable rows={data.audit || []} />
    </section>
  );
};

const AuditTable = ({ rows }) => (
  <div className="mt-4 max-h-72 overflow-auto" data-testid="sso-audit-table">
    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-700">Last 50 attempts</p>
    <table className="w-full min-w-[480px] text-xs">
      <thead className="bg-sand-100 text-left text-ink-700">
        <tr><th className="p-2">Time</th><th className="p-2">Phone</th><th className="p-2">Result</th><th className="p-2">Host</th><th className="p-2">Created?</th></tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-t border-border" data-testid={`sso-audit-row-${r.id}`}>
            <td className="p-2 font-mono">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
            <td className="p-2 font-mono">{r.phoneLast4 ? `••${r.phoneLast4}` : "—"}</td>
            <td className="p-2"><span className={`rounded-full px-2 py-0.5 font-semibold ${r.result === "success" ? "bg-moss/15 text-moss-dark" : "bg-rose-100 text-rose-700"}`}>{r.result}</span></td>
            <td className="p-2">{r.host}</td>
            <td className="p-2">{r.created ? "yes" : "—"}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-ink-700" data-testid="sso-audit-empty">No auto-login attempts yet.</td></tr>}
      </tbody>
    </table>
  </div>
);
